import { motion } from 'framer-motion';
import AnimatedSection from '../common/AnimatedSection';
import useAPI from '../../hooks/useAPI';
import Loading from '../Loading/Loading';

const About = () =>{
  const {
    data: aboutData,
    loading,
    error,
  } = useAPI('/about');

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }
  return(
    <>
      {aboutData?.data?.map((item,index)=>(
        <section key={index} id="about" className="py-24 md:py-32 bg-white overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">

            {/* ── Label + Heading ── */}
            <AnimatedSection>
              <p className="text-primary text-[11px] font-black tracking-[0.22em] uppercase text-center mb-5">
                About Us
              </p>
              <h2 className="text-4xl md:text-5xl lg:text-[3.5rem] font-black text-dark text-center leading-[1.1] tracking-tight max-w-3xl mx-auto">
                {/* Building the{' '}
                <em className="not-italic text-primary">next generation</em>
                {' '}of industry leaders */}
                {item?.title}
              </h2>
              <p className="mt-5 text-dark/45 text-center text-base max-w-lg mx-auto leading-relaxed">
                {item?.subtitle}
              </p>
            </AnimatedSection>

            {/* ── Main grid ── */}
            <div className="mt-20 grid lg:grid-cols-2 gap-16 items-start">

              {/* ── LEFT: Image + Stats ── */}
              <AnimatedSection delay={0.1}>
                <div className="space-y-4">

                  {/* clean image */}
                  <div className="relative aspect-[4/3] rounded-2xl overflow-hidden">
                    <img
                      src={item?.image || "btilogo.jpeg"}
                      alt="BTI"
                      className="w-full h-full object-cover"
                    />
                    {/* very subtle bottom gradient so stats cards sit above it visually */}
                    <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/30 to-transparent" />
                  </div>

                  {/* ── Stats: sharp bottom cards ── */}
                  <div className="grid grid-cols-3 divide-x divide-slate-100 border border-slate-100 rounded-2xl overflow-hidden bg-white shadow-sm shadow-slate-100">
                    {item?.stats?.map((s, i) => (
                      <motion.div
                        key={i}
                        whileHover={{ backgroundColor: 'rgb(249 250 251)' }}
                        transition={{ duration: 0.15 }}
                        className="group flex flex-col items-center justify-center py-5 px-3 text-center cursor-default"
                      >
                        <span className="text-[1.75rem] font-black tracking-tight text-dark leading-none group-hover:text-primary transition-colors duration-200">
                          {s.number}
                        </span>
                        <span className="mt-1.5 text-[10px] font-semibold text-dark/35 uppercase tracking-[0.12em] leading-tight">
                          {s.label}
                        </span>
                        {/* bottom accent line on hover */}
                        <motion.div
                          className="mt-3 h-[2px] w-6 bg-primary rounded-full"
                          initial={{ scaleX: 0, opacity: 0 }}
                          whileHover={{ scaleX: 1, opacity: 1 }}
                          transition={{ duration: 0.2 }}
                        />
                      </motion.div>
                    ))}
                  </div>

                </div>
              </AnimatedSection>

              {/* ── RIGHT: Text + Highlights ── */}
              <AnimatedSection delay={0.15}>
                <div className="space-y-10">

                  {/* body text */}
                  <div
                    className="space-y-4 text-dark/55 text-[0.97rem] leading-[1.9]"
                    dangerouslySetInnerHTML={{
                      __html: item?.description,
                    }}
                  />

                  {/* ── Highlights ── */}
                  <div className="space-y-3">
                    {item?.features?.map((feat, inde) => (
                      <AnimatedSection key={inde} delay={0.2 + inde * 0.08}>
                        <motion.div
                          whileHover={{ x: 4 }}
                          transition={{ type: 'spring', stiffness: 350, damping: 24 }}
                          className="group flex items-center gap-4 px-5 py-4 rounded-xl border border-slate-100 hover:border-primary/20 hover:shadow-md hover:shadow-primary/[0.06] bg-white transition-all duration-200 cursor-default"
                        >
                          {/* icon box */}
                          {/* <div className="w-10 h-10 rounded-lg bg-slate-50 text-dark/40 flex items-center justify-center flex-shrink-0 group-hover:bg-primary group-hover:text-white transition-all duration-200">
                            {item.icon}
                          </div> */}

                          {/* text */}
                          <div className="flex-1 min-w-0">
                            <p className="text-[0.88rem] font-bold text-dark leading-tight">{feat.title}</p>
                            <p className="text-xs text-dark/35 mt-0.5">{feat.description}</p>
                          </div>

                          {/* right arrow */}
                          <svg className="w-4 h-4 text-slate-200 group-hover:text-primary/40 flex-shrink-0 transition-colors duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                          </svg>
                        </motion.div>
                      </AnimatedSection>
                    ))}
                  </div>

                </div>
              </AnimatedSection>

            </div>
          </div>
        </section>
      ))}
    </>
  )
}

export default About;